import {Box, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useId} from 'react';

import {always} from 'ramda';
import {isDate, isNotNil, isString} from 'ramda-adjunct';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {TimeType} from '../../types/TimeType';
import {getTimeFromDate} from '../../utils/getTimeFromDate';
import {setTimeOnDate} from '../../utils/setTimeOnDate';
import {DatePicker} from '../DatePicker/DatePicker';
import {Label} from '../Label/Label';
import {TimePicker} from '../TimePicker/TimePicker';

export interface DateTimePickerProps extends FormControlProps<Date | null>, TestIdProps {
  minDate?: Date;
  maxDate?: Date;
  isRelativeDatesHidden?: boolean;
  isNonClearable?: boolean;
}

export function DateTimePicker(props: DateTimePickerProps) {
  const id = useId();
  const time = isNotNil(props.value) ? getTimeFromDate(props.value) : null;

  const onDateChange = (date: Date | null) => {
    if (!date) {
      props.onChange?.(null);
      return;
    }

    const dateTime = time ? setTimeOnDate(date, time) : date;
    props.onChange?.(dateTime);
  };

  const onTimeChange = (value: TimeType | null) => {
    if (!value) {
      props.onChange?.(null);
      return;
    }

    const dateValue = match<Date | null, Date>(props.value)
      .when(isDate, (value) => value)
      .when(isString, (value) => parseDate(value))
      .otherwise(always(new Date()));

    const dateTime = setTimeOnDate(dateValue, value);
    props.onChange?.(dateTime);
  };

  return (
    <VStack>
      <Label
        id={id}
        data-testid={suffixTestId('label', props)}
        tooltip={props.tooltip}
        isRequired={props.isRequired}
      >
        {props.label}
      </Label>
      <HStack spacing={2}>
        <Box flex={1}>
          <DatePicker
            id={id}
            isDisabled={props.isDisabled}
            minDate={props.minDate}
            maxDate={props.maxDate}
            onChange={onDateChange}
            value={props.value}
            isRelativeDatesHidden={props.isRelativeDatesHidden}
            helperText={props.helperText}
            errorMessage={props.errorMessage}
            isInvalid={props.isInvalid}
            data-testid={suffixTestId('datePicker', props)}
            isNonClearable={props.isNonClearable}
          />
        </Box>
        <Box flex={1}>
          <TimePicker
            isInvalid={props.isInvalid}
            value={time}
            onChange={onTimeChange}
            isDisabled={props.isDisabled}
            data-testid={suffixTestId('timePicker', props)}
            isNonClearable={props.isNonClearable}
          />
        </Box>
      </HStack>
    </VStack>
  );
}
